import React, { useState, useEffect } from 'react'
import { Card, Spin } from 'antd'
import styled from 'styled-components'
import {
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import dayjs from 'dayjs'

// 会员方案样式组件
const MembershipContainer = styled.div`
  padding: 0;
`

const MembershipHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  h2 {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .crown-icon {
    font-size: 32px;
    color: #d5728a;
    margin-right: 16px;
  }
`

const CurrentPlanCard = styled(Card)`
  margin-bottom: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: none;
  overflow: hidden;

  .ant-card-body {
    padding: 32px;
  }

  background: linear-gradient(135deg, #fff5f7 0%, #ffeef2 100%);
`

const PlanBadge = styled.div`
  display: inline-block;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;

  &.basic {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
  }

  &.business {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1e40af;
  }

  &.brand {
    background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
    color: #9f1239;
  }

  &.free {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    color: #374151;
  }
`

const PlanTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`

const PlanDetails = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 24px;
  flex-wrap: wrap;

  .detail-item {
    display: flex;
    flex-direction: column;

    .label {
      font-size: 12px;
      color: #8c8c8c;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 16px;
      color: #1a1a1a;
      font-weight: 500;
    }
  }
`

const ComparisonTable = styled.div`
  margin-top: 40px;
`

const ComparisonTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 24px;
`

const TableCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: none;
  overflow: hidden;

  .ant-card-body {
    padding: 0;
    overflow-x: auto;

    @media (max-width: 768px) {
      overflow-x: scroll;
      -webkit-overflow-scrolling: touch;
    }
  }
`

const FeatureTable = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;

  thead {
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);

    th {
      padding: 24px 16px;
      text-align: center;
      font-weight: 600;
      color: #1a1a1a;
      font-size: 14px;
      border-bottom: 2px solid #e8e8e8;
      position: relative;

      &:first-child {
        text-align: left;
        padding-left: 24px;
        background: transparent;
      }

      &.current-plan {
        background: linear-gradient(135deg, #fff5f7 0%, #ffeef2 100%);
        border-bottom: 2px solid #d5728a;

        &::after {
          content: '目前方案';
          position: absolute;
          top: 8px;
          right: 8px;
          background: #d5728a;
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
        }
      }

      .plan-name {
        font-size: 18px;
        margin-bottom: 8px;
        font-weight: 600;
      }

      .plan-price {
        font-size: 24px;
        font-weight: 700;
        color: #d5728a;
        margin: 8px 0;
      }

      .plan-detail {
        font-size: 12px;
        color: #8c8c8c;
        font-weight: normal;
        margin-top: 4px;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;

      &:hover {
        background: #fafafa;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 16px;
      text-align: center;
      color: #595959;
      font-size: 14px;

      &:first-child {
        text-align: left;
        padding-left: 24px;
        font-weight: 500;
        color: #1a1a1a;
      }

      .feature-icon {
        font-size: 18px;
        font-weight: 600;

        &.included {
          color: #52c41a;
        }

        &.premium {
          color: #d5728a;
          font-weight: 700;
        }

        &.not-included {
          color: #d9d9d9;
        }
      }
    }
  }

  @media (max-width: 768px) {
    font-size: 12px;

    thead th {
      padding: 12px 8px;

      .plan-name {
        font-size: 14px;
      }

      .plan-price {
        font-size: 18px;
      }
    }

    tbody td {
      padding: 12px 8px;
    }
  }
`

const MembershipInfoPage = () => {
  const { user } = useAuth()
  const [businessInfo, setBusinessInfo] = useState({
    plan: 'free',
    endDate: null,
    startDate: null,
    duration: null,
    durationUnit: 'month'
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await api.get(`/api/business_B/${user.businessId}`)
        setBusinessInfo({
          plan: response.data.subscription.plan,
          endDate: response.data.subscription.endDate,
          startDate: response.data.subscription.startDate,
          duration: response.data.subscription.duration,
          durationUnit: response.data.subscription.durationUnit || 'month'
        })
      } catch (error) {
        console.error('獲取商家資訊時出錯：', error)
      } finally {
        setLoading(false)
      }
    }

    fetchBusinessInfo()
  }, [user.businessId])

  const getMembershipTierName = (tier) => {
    const tiers = {
      free: '試用版',
      basic: '一般方案',
      brand: '高階方案',
      business: '商務方案（舊方案）'
    }
    return tiers[tier] || tier
  }

  // 兩方案制功能列表（一般 = basic、高階 = brand；舊商務方案客戶權益不變）
  const membershipFeatures = [
    // ── 一般方案就有的基礎功能 ──
    { name: '會員專屬後台 + APP', basic: true, brand: true },
    {
      name: '自動化預約系統（含預約人數上限、銜接時間）',
      basic: true,
      brand: true
    },
    {
      name: '顧客管理系統（標籤、進階搜索、消費紀錄）',
      basic: true,
      brand: true
    },
    {
      name: '班表（月排班表、自訂班別與假別、休假月曆）',
      basic: true,
      brand: true
    },
    { name: 'LINE 通知整合（預約確認／行前提醒）', basic: true, brand: true },
    { name: '優惠券模組', basic: true, brand: true },
    { name: '顧客評價回覆', basic: true, brand: true },
    { name: '定金與收款設定', basic: true, brand: true },
    { name: '團隊排班管理', basic: true, brand: true },
    { name: '作品集展示', basic: '20 張', brand: '無限' },
    // ── 高階方案獨享 ──
    { name: '營運報表與數據分析', basic: false, brand: true },
    { name: '結帳 POS 與結帳紀錄', basic: false, brand: true },
    { name: '儲值金管理（含轉移、餘額調整）', basic: false, brand: true },
    { name: '薪資與業績抽成管理', basic: false, brand: true },
    { name: '庫存管理（進出貨完整追溯）', basic: false, brand: true },
    {
      name: '線上商店下單（含規格庫存、宅配／到店取貨）',
      basic: false,
      brand: true
    },
    { name: '堂票模組（含銷售抽成）', basic: false, brand: true },
    { name: '項目施作同意書（線上簽署）', basic: false, brand: true },
    {
      name: '施作照片紀錄（客人可在預約紀錄查看）',
      basic: false,
      brand: true
    },
    {
      name: '分期收款（店內自行分期、逾期追蹤、應收帳款報表）',
      basic: false,
      brand: true
    },
    { name: '回訪提醒（可自動發送）', basic: false, brand: true },
    {
      name: '自訂會員等級／顧客群組（自動升降等）',
      basic: false,
      brand: true
    },
    { name: 'Google 預訂', basic: false, brand: true },
    { name: 'Google 日曆同步', basic: false, brand: true },
    { name: '招聘與媒合', basic: false, brand: true },
    { name: 'AI 優化器', basic: false, brand: true },
    { name: '平台推廣與品牌曝光', basic: false, brand: true }
  ]

  if (loading) {
    return (
      <Spin
        size='large'
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      />
    )
  }

  const expiryDate = businessInfo.endDate
    ? dayjs(businessInfo.endDate).format('YYYY/MM/DD')
    : '永久'

  const startDate = businessInfo.startDate
    ? dayjs(businessInfo.startDate).format('YYYY/MM/DD')
    : '尚未加入方案'

  const currentPlanName = getMembershipTierName(businessInfo.plan)

  return (
    <MembershipContainer>
      <MembershipHeader>
        <CrownOutlined className='crown-icon' />
        <h2>會員方案資訊</h2>
      </MembershipHeader>

      <CurrentPlanCard>
        <PlanBadge className={businessInfo.plan}>{currentPlanName}</PlanBadge>
        <PlanTitle>
          {currentPlanName}
          {businessInfo.duration && ` · ${businessInfo.duration}${businessInfo.durationUnit === 'day' ? '天' : '個月'}`}
        </PlanTitle>
        <PlanDetails>
          <div className='detail-item'>
            <span className='label'>起始日期</span>
            <span className='value'>{startDate}</span>
          </div>
          <div className='detail-item'>
            <span className='label'>到期日期</span>
            <span className='value'>{expiryDate}</span>
          </div>
        </PlanDetails>
      </CurrentPlanCard>

      <ComparisonTable>
        <ComparisonTitle>方案比較</ComparisonTitle>
        <TableCard>
          <FeatureTable>
            <thead>
              <tr>
                <th>權益項目</th>
                <th
                  className={
                    businessInfo.plan === 'basic' ? 'current-plan' : ''
                  }
                >
                  <div className='plan-name'>一般方案</div>
                  <div className='plan-price'>NT$ 1,400/月</div>
                  <div className='plan-detail'>服務人員：3 人</div>
                </th>
                <th
                  className={
                    businessInfo.plan === 'brand' ? 'current-plan' : ''
                  }
                >
                  <div className='plan-name'>高階方案</div>
                  <div className='plan-price'>NT$ 3,800/月</div>
                  <div className='plan-detail'>服務人員：6 人</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {membershipFeatures.map((feature, index) => (
                <tr key={index}>
                  <td>{feature.name}</td>
                  <td>
                    {typeof feature.basic === 'string' ? (
                      <span className={`feature-icon premium`}>
                        {feature.basic}
                      </span>
                    ) : feature.basic ? (
                      <CheckCircleOutlined className='feature-icon included' />
                    ) : (
                      <CloseCircleOutlined className='feature-icon not-included' />
                    )}
                  </td>
                  <td>
                    {typeof feature.brand === 'string' ? (
                      <span className={`feature-icon premium`}>
                        {feature.brand}
                      </span>
                    ) : feature.brand ? (
                      <CheckCircleOutlined className='feature-icon included' />
                    ) : (
                      <CloseCircleOutlined className='feature-icon not-included' />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </FeatureTable>
        </TableCard>
      </ComparisonTable>
    </MembershipContainer>
  )
}

export default MembershipInfoPage
